/* eslint-disable no-param-reassign */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Divider, Grid, Paper, Stack, Link, Box, Tab, Tabs, Typography, alpha, Tooltip } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import HelpCenter from '@mui/icons-material/HelpCenter';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import dayjs from 'dayjs';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import { FormErrorSummary } from 'src/shared/components/form/formErrorSummary';
import { toast } from 'react-toastify';
import { useNavigate, useParams, useBlocker } from 'react-router-dom';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useGlobals } from 'src/shared/contexts/appContext';
import { SelectionForm } from 'src/modules/groupMessageEmail/components/selectionForm';
import { GroupMessageRequestPost } from 'src/modules/groupMessageEmail/models/groupMessageRequestPost';
import { createGroupMessage, getGroupMessageEmailTemplateById, getGroupMessageEmailTemplateByName } from 'src/shared/services/groupMessageService';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { ControlledFileDropZone } from 'src/shared/components/form/fileDropZone';
import { ContentSection } from 'src/shared/components/organisms/contentSection';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { removeBase64Images } from 'src/shared/functions/removeBase64Images';
import { isDesignModeCompatible } from 'src/modules/groupMessageEmail/builder/parse';

import { getSenderTitle } from 'src/shared/functions/getSenderTitle';
import { AddCircle, DashboardCustomize, Drafts, Edit, Pageview, Save } from '@mui/icons-material';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { SelectionTypeEnum } from '../models/selectionType.enum';
import { RemovedImagesDialog } from '../components/removedImagesDialog';
import { FixedSelection } from '../models/fixedSelection';
import { Selection } from '../models/selection';
import { useSchemaCreateGroupMessageEmail } from '../schemas/useSchemaCreateGroupMessageEmail';
import { GroupMessageEmailPreviewModal } from '../components/groupMessageEmailPreviewModal';
import { GroupMessageEmailTemplateCreateModal } from '../components/groupMessageEmailTemplateCreateModal';
import { GroupMessageEmailTemplateView } from '../components/groupMessageEmailTemplateView';
import { EmailBuilderModal, EmailBuilderMode } from '../components/emailBuilderModal';
import { SendSplitButton } from '../components/sendSplitButton';
import { SendMessageModal } from '../components/sendMessageModal';
import { attachmentsExceedMax, shouldSendAttachmentsAsLinks } from '../functions/attachmentSize';

const GroupMessageEmailCreatePage = () => {
  const params = useParams<{ id: string }>();
  const templateId = Number(params.id);
  const { currentUser } = useGlobals();
  const { t } = useTranslation(['groupMessage', 'common']);
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
  const [builderMode, setBuilderMode] = useState<EmailBuilderMode | null>(null);
  const [previewTab, setPreviewTab] = useState<'preview' | 'edit'>('edit');
  const [sendMode, setSendMode] = useState<boolean>(false);
  const [openCreateEmailTemplateModal, setOpenCreateEmailTemplateModal] = useState<boolean>(false);
  const [removedImages, setRemovedImages] = useState<string[]>([]);
  const [dataToSave, setDataToSave] = useState<GroupMessageRequestPost>();
  const [, setFormErrors] = useState<any>();
  // Send-modal: visa sammanfattning + preview innan användaren bekräftar utskick eller schemaläggning.
  const [sendModalOpen, setSendModalOpen] = useState<boolean>(false);
  const [scheduleMode, setScheduleMode] = useState<'now' | 'schedule'>('now');

  const [selection, setSelection] = useState<Selection | null>(null);
  const [fixedSelection, setFixedSelection] = useState<FixedSelection | null>(null);

  // const [files, setFiles] = useState<File[]>([]);
  // const onFilesChange = (newFiles: File[]) => {
  //   setFiles(newFiles);
  // };

  const isMounted = useIsMounted();

  const createGroupMessageEmailSchema = useSchemaCreateGroupMessageEmail();
  type CreateGroupMessageFormInterface = yup.InferType<typeof createGroupMessageEmailSchema>;

  const formMethods = useForm<CreateGroupMessageFormInterface>({
    resolver: yupResolver(createGroupMessageEmailSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      subject: '',
      sender: currentUser?.email || '',
      speedFormValues: '',
      speedFormText: '',
      senderTitle: getSenderTitle(currentUser),
      selectionType: SelectionTypeEnum.Selection,
      circularName: null,
      body: '',
      recipients: '',
      selection_id: 0,
      files: [],
      isHTML: true,
      send: false,
      sendDate: null,
      excludedMemberFromSelection: [],
    },
  });

  const {
    handleSubmit,
    setValue,
    watch,
    getValues,
    reset,
    formState: { errors, isSubmitting, isValid },
    formState,
    trigger,
  } = formMethods;

  // Designläge är bara säkert för utskick byggda med designeditorn (har data-email-doc-markör).
  // Legacy HTML korrumperas vid round-trip.
  const designModeAvailable = isDesignModeCompatible(watch('body'));
  // För design-mallar finns ingen "Redigera innehåll"-flik — då tvingar vi alltid preview.
  const effectivePreviewTab = designModeAvailable ? 'preview' : previewTab;

  // Save-flödet sätter denna till true precis innan navigate så vi inte triggar leave-dialogen.
  const finalizingSaveRef = useRef(false);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (finalizingSaveRef.current) return false;
    if (isSubmitting) return false;
    return currentLocation.pathname !== nextLocation.pathname;
  });

  // Tab-close / hård refresh
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!isSubmitting && !finalizingSaveRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isSubmitting]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let res;
      if (!templateId) {
        res = await getGroupMessageEmailTemplateByName(params.id ?? '');
      } else {
        res = await getGroupMessageEmailTemplateById(templateId || 0);
      }
      if (isMounted()) {
        if (res) {
          setValue('body', res.data);
        }
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      setLoading(false);
    }
  }, [isMounted, params.id, setValue, templateId]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  const postData = useCallback(
    async (data) => {
      ConsoleHelper.log('postData', data);
      try {
        data.send = sendMode;
        data.circularName = data.subject;
        // Hantera schemaläggning: vid schemaläggning skicka lokal tid i ISO-format (matchar DateTime.Now-konventionen i backend).
        // Vid "Skicka nu" eller bara spara: nolla sendDate så backend använder default-beteendet.
        if (sendMode && scheduleMode === 'schedule' && data.sendDate) {
          data.sendDate = dayjs(data.sendDate).format('YYYY-MM-DDTHH:mm:ss');
        } else {
          data.sendDate = null;
        }
        const res = await createGroupMessage(data);
        ConsoleHelper.log('res: ', res);
        if (isMounted()) {
          if (sendMode && scheduleMode === 'schedule') {
            toast.success(t('groupMessage:create.scheduleSuccess', { when: dayjs(data.sendDate).format('YYYY-MM-DD HH:mm') }) as string);
          } else if (sendMode) {
            toast.success(t('groupMessage:create.sendSuccess') as string);
          } else {
            toast.success(t('groupMessage:create.saveSuccess') as string);
          }
          finalizingSaveRef.current = true;
          setSendModalOpen(false);
          // Vid Skicka/Schemalägg → tillbaka till översikten.
          // Vid bara Spara (utkast) → stanna kvar genom att navigera till edit-vyn för det nyskapade utskicket.
          if (sendMode) {
            navigate(RoutesEnum.GroupMessageEmailOverview);
          } else {
            // Backend returnerar circular_id i BaseResponse.Data (PascalCase eller camelCase beroende på serializer)
            const payload = res?.data?.Data ?? res?.data?.data;
            const newId = typeof payload === 'number' ? payload : payload?.circular_id;
            if (newId) {
              navigate(`${RoutesEnum.GroupMessageEmailEdit}/${newId}`);
            } else {
              navigate(RoutesEnum.GroupMessageEmailOverview);
            }
          }
        }
      } catch (e) {
        if (isMounted()) {
          if (data.send) toast.warning(t('groupMessage:create.sendError') as string);
          else toast.warning(t('groupMessage:create.saveError') as string);
          ConsoleHelper.error('saveFormDataError:', e);
        }
      }
    },
    [isMounted, navigate, scheduleMode, sendMode, t],
  );

  const saveFormData = useCallback(
    async (data: GroupMessageRequestPost) => {
      ConsoleHelper.log('saveFormData', data);
      const { modifiedContent, strippedImages } = removeBase64Images(data.body);
      // Spara arrayen med bilder i statet
      setRemovedImages(strippedImages);
      if (strippedImages.length > 0) {
        data.body = modifiedContent;
        ConsoleHelper.log('modifiedContent', modifiedContent);
        setValue('body', modifiedContent);
        setDataToSave(data);
      } else {
        await postData(data);
      }
    },
    [postData, setValue],
  );

  useEffect(() => {
    if (formState.errors) {
      setFormErrors(formState.errors);
    }
  }, [formState]);

  const onSubmit = async (data: CreateGroupMessageFormInterface) => {
    const { excludedMemberFromSelection, ...rest } = data;

    // Blockera om bilagorna överstiger det hårda taket – hela requesten måste rymmas i serverns gräns (work item 6051).
    if (attachmentsExceedMax(rest.files)) {
      setSendModalOpen(false);
      toast.warning(t('groupMessage:create.attachmentsTooLarge') as string);
      return;
    }

    const dataToPost: GroupMessageRequestPost = {
      ...rest,
      includeRecipientsThatHasUnsubscribedFromEmail: false,
      // Skicka bilagor som länkar när totala storleken överstiger 10 MB (work item 6051).
      attachmentsAsLinks: shouldSendAttachmentsAsLinks(rest.files),
      excludeMemberIds: excludedMemberFromSelection?.map((member) => member.member_id).filter((id): id is number => id !== undefined) || [],
    };

    ConsoleHelper.log('dataToPost', dataToPost);

    await trigger();
    await saveFormData(dataToPost);
  };

  const handleConfirmImageRemoval = () => {
    postData(dataToSave);
  };

  const returnToOverview = () => {
    navigate(RoutesEnum.GroupMessageEmailOverview);
  };

  // Öppna send-modalen efter att formuläret validerats. Markerar sendMode=true så onSubmit kör utskick.
  const openSendModal = useCallback(
    async (mode: 'now' | 'schedule') => {
      const valid = await trigger();
      if (!valid) return;
      // Öppna inte skicka-modalen om bilagorna överstiger det hårda taket.
      if (attachmentsExceedMax(getValues('files'))) {
        toast.warning(t('groupMessage:create.attachmentsTooLarge') as string);
        return;
      }
      setScheduleMode(mode);
      setSendMode(true);
      setSendModalOpen(true);
    },
    [trigger, getValues, t],
  );

  // Knapparna återanvänds både i sidans header och längst ned vid formuläret
  const actionButtons = (
    <Stack direction='row' spacing={1} alignItems='center'>
      <Button
        variant='outlined'
        type='button'
        disabled={Object.keys(errors).length > 0 && !isValid}
        onClick={async () => {
          await trigger();
          if (isValid) setShowPreviewModal(true);
        }}
        startIcon={<Drafts aria-hidden />}
      >
        {t('groupMessage:builder.sendTestMail')}
      </Button>
      <Button variant='outlined' type='button' startIcon={<AddCircle />} onClick={() => setOpenCreateEmailTemplateModal(true)} disableElevation>
        {t('groupMessage:create.createNewTemplate')}
      </Button>
      <LoadingButton
        loading={isSubmitting}
        variant='contained'
        type='submit'
        disableElevation
        onClick={() => setSendMode(false)}
        disabled={Object.keys(errors).length > 0 && !isValid}
        startIcon={<Save aria-hidden />}
      >
        {t('common:button.save')}
      </LoadingButton>
      <Divider orientation='vertical' variant='middle' flexItem />
      <SendSplitButton
        loading={isSubmitting}
        disabled={Object.keys(errors).length > 0 || (!selection && !fixedSelection)}
        onSendNow={() => openSendModal('now')}
        onSchedule={() => openSendModal('schedule')}
      />
    </Stack>
  );

  return (
    <>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <GridWrapper fixed>
            <LoadingOverlay loading={loading} />
            <Grid
              item
              xs={12}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16, marginBottom: -10 }}
            >
              <MainContentHeader title={t('groupMessage:create.header')} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Link
                  href='https://hjalp.foreningssupport.se/article/312-kommunikation-e-postutskick#Skapa-ett-nytt-e-postutskick-mWATY'
                  target='_blank'
                  rel='noopener'
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                >
                  <HelpCenter fontSize='small' />
                  {t('groupMessage:create.helpLinkText')}
                </Link>
                {actionButtons}
              </Box>
            </Grid>
            {removedImages && removedImages.length > 0 && (
              <RemovedImagesDialog
                removedImages={removedImages}
                open
                handleClose={() => setRemovedImages([])}
                confirmImageRemoval={handleConfirmImageRemoval}
              />
            )}
            <GroupMessageEmailPreviewModal
              modalOpen={showPreviewModal}
              onClose={() => setShowPreviewModal(false)}
              onSave={() => {
                setShowPreviewModal(false);
                // Test-mail-send sparar innehållet på backend. Återbaselina formuläret så
                // useBlocker inte varnar om "osparade ändringar" och låt användaren stanna
                // kvar på utskicket istället för att navigera bort.
                reset(getValues());
              }}
            />
            <EmailBuilderModal open={builderMode !== null} mode={builderMode ?? 'design'} onClose={() => setBuilderMode(null)} />
            <SendMessageModal
              open={sendModalOpen}
              mode={scheduleMode}
              loading={isSubmitting}
              recipientsLabel={selection?.selectionName || fixedSelection?.name || undefined}
              onClose={() => setSendModalOpen(false)}
              onConfirm={async () => {
                await handleSubmit(onSubmit)();
              }}
            />
            <DeleteConfirmationDialog
              open={blocker.state === 'blocked'}
              title={t('groupMessage:create.unsavedChangesTitle')}
              modaltext={t('groupMessage:create.unsavedChangesText')}
              value='leave'
              confirmButtonText={t('groupMessage:create.leaveAnyway')}
              onClose={(result) => {
                if (result?.id === 'leave') {
                  finalizingSaveRef.current = true;
                  blocker.proceed?.();
                } else {
                  blocker.reset?.();
                }
              }}
            />
            <Grid item xs={12}>
              <FormErrorSummary translationNamespace='groupMessage' errors={errors} />
            </Grid>
            <Grid item xs={12}>
              <Paper
                sx={{
                  p: 3,
                }}
              >
                <GridWrapper>
                  <Grid item xs={12} lg={4}>
                    <Stack spacing={2} useFlexGap sx={{ mt: 0 }}>
                      <SelectionForm
                        selectionFromServer={null}
                        fixedSelectionFromServer={null}
                        onSelection={(v) => setSelection(v)}
                        onFixedSelection={(v) => setFixedSelection(v)}
                        headerVariant='subtitle1'
                        headerFontWeight={600}
                      />

                      <ControlledTextField
                        name='recipients'
                        label={t('groupMessage:formFields.recipients')}
                        helperText={t('groupMessage:formFields.recipients_helperText')}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />

                      <ContentSection header={t('groupMessage:sections.senderAndSubject')} headerVariant='subtitle1' headerFontWeight={600}>
                        <Stack spacing={2} sx={{ pt: 1 }}>
                          <ControlledTextField
                            name='subject'
                            required
                            label={t('groupMessage:formFields.subject')}
                            inputProps={{ maxLength: 70 }}
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                          <ControlledTextField
                            name='senderTitle'
                            required
                            label={t('groupMessage:formFields.senderTitle')}
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                          <ControlledTextField
                            name='sender'
                            required
                            label={t('groupMessage:formFields.sender')}
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                        </Stack>
                      </ContentSection>

                      <ContentSection header={t('groupMessage:sections.attachments')} headerVariant='subtitle1' headerFontWeight={600}>
                        <ControlledFileDropZone name='files' disabled={false} documentFiles={undefined} orientation='vertical' multiple />
                      </ContentSection>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} lg={8}>
                    <Stack spacing={2} useFlexGap sx={{ mt: 0 }}>
                      <ContentSection>
                        {/* Tab-rad: Redigera innehåll / Förhandsgranska till vänster, Designläge till höger */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            borderBottom: 1,
                            borderColor: 'divider',
                            px: 1,
                          }}
                        >
                          <Tabs
                            value={designModeAvailable ? 'preview' : previewTab}
                            onChange={(_, value: 'preview' | 'edit') => setPreviewTab(value)}
                            sx={{ minHeight: 'auto' }}
                          >
                            {!designModeAvailable && (
                              <Tab
                                value='edit'
                                label={t('groupMessage:sections.editContent')}
                                icon={<Edit fontSize='small' />}
                                iconPosition='start'
                                sx={{ minHeight: 36, py: 0 }}
                              />
                            )}
                            <Tab
                              value='preview'
                              label={t('groupMessage:builder.previewTab')}
                              icon={<Pageview fontSize='small' />}
                              iconPosition='start'
                              sx={{ minHeight: 36, py: 0 }}
                            />
                          </Tabs>
                          <Tooltip title={!designModeAvailable ? (t('groupMessage:builder.designModeLockedTooltip') as string) : ''}>
                            <span>
                              <Button
                                size='small'
                                variant='outlined'
                                startIcon={<DashboardCustomize fontSize='small' />}
                                onClick={() => setBuilderMode('design')}
                                disabled={!designModeAvailable}
                              >
                                {t('groupMessage:builder.designMode')}
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>

                        {effectivePreviewTab === 'preview' && (
                          <Box sx={{ pt: 2 }}>
                            <Box
                              sx={{
                                position: 'relative',
                                width: '100%',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'background.paper',
                                overflow: 'hidden',
                                boxShadow: 1,
                                transition: 'box-shadow 120ms ease',
                                '&:hover': {
                                  boxShadow: 3,
                                  '& .builder-overlay': {
                                    opacity: 1,
                                    pointerEvents: 'auto',
                                  },
                                },
                              }}
                            >
                              {/* Mail-header i Gmail/Outlook-stil */}
                              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                                <Typography
                                  variant='h6'
                                  sx={{
                                    lineHeight: 1.3,
                                    mb: 1,
                                    fontWeight: 500,
                                    color: watch('subject') ? 'text.primary' : 'text.disabled',
                                  }}
                                >
                                  {watch('subject') || t('groupMessage:preview.subjectPlaceholder')}
                                </Typography>
                                <Stack direction='row' alignItems='center' spacing={1.5}>
                                  <Box
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      borderRadius: '50%',
                                      bgcolor: 'primary.main',
                                      color: 'primary.contrastText',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.875rem',
                                      fontWeight: 600,
                                      flexShrink: 0,
                                    }}
                                  >
                                    {(watch('senderTitle') || '?').charAt(0).toUpperCase()}
                                  </Box>
                                  <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography
                                      variant='body2'
                                      sx={{
                                        fontWeight: 500,
                                        color: watch('senderTitle') ? 'text.primary' : 'text.disabled',
                                        lineHeight: 1.3,
                                      }}
                                    >
                                      {watch('senderTitle') || t('groupMessage:preview.senderTitlePlaceholder')}
                                    </Typography>
                                    <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                                      {watch('sender') || ''}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </Box>

                              <GroupMessageEmailTemplateView body={watch('body') || ''} height='40rem' />

                              {/* Hover-overlay med centrerade edit-knappar */}
                              <Box
                                className='builder-overlay'
                                sx={{
                                  position: 'absolute',
                                  inset: 0,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 2,
                                  bgcolor: (theme) => alpha(theme.palette.common.black, 0.45),
                                  opacity: 0,
                                  pointerEvents: 'none',
                                  transition: 'opacity 150ms ease',
                                }}
                              >
                                <Stack direction='row' spacing={2}>
                                  {!designModeAvailable && (
                                    <Button
                                      variant='contained'
                                      size='large'
                                      startIcon={<Edit />}
                                      disableElevation
                                      onClick={() => setPreviewTab('edit')}
                                      sx={{ boxShadow: 4 }}
                                    >
                                      {t('groupMessage:sections.editContent')}
                                    </Button>
                                  )}
                                  <Tooltip title={!designModeAvailable ? (t('groupMessage:builder.designModeLockedTooltip') as string) : ''}>
                                    <span>
                                      <Button
                                        variant='contained'
                                        size='large'
                                        color='inherit'
                                        startIcon={<DashboardCustomize />}
                                        onClick={() => setBuilderMode('design')}
                                        disabled={!designModeAvailable}
                                        sx={{
                                          bgcolor: 'background.paper',
                                          color: 'text.primary',
                                          boxShadow: 4,
                                          '&:hover': { bgcolor: 'background.paper' },
                                        }}
                                      >
                                        {t('groupMessage:builder.editInDesignMode')}
                                      </Button>
                                    </span>
                                  </Tooltip>
                                </Stack>
                              </Box>
                            </Box>
                          </Box>
                        )}

                        {effectivePreviewTab === 'edit' && (
                          <Box sx={{ pt: 2 }}>
                            <ControlledTextEditor name='body' noMargin resizeAllowed rows={50} />
                          </Box>
                        )}
                      </ContentSection>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} textAlign='right' sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {actionButtons}
                  </Grid>
                </GridWrapper>
              </Paper>
            </Grid>
          </GridWrapper>
        </form>
      </FormProvider>
      <GroupMessageEmailTemplateCreateModal body={watch('body')} open={openCreateEmailTemplateModal} openModal={setOpenCreateEmailTemplateModal} />
    </>
  );
};

export default GroupMessageEmailCreatePage;
